import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const events = [
  {
    image: 'https://upload.wikimedia.org/wikipedia/commons/e/e4/Frecce_Tricolori_2022.jpg',
    tag: 'Celebration',
    day: '07',
    month: 'JUN 2026',
    title: 'Italian National Day',
    desc: 'The Italian National Day (Festa della Repubblica) is celebrated in Dhaka every year to mark the birth of the Italian Republic.',
    hasRegistration: false,
  },
 
];

export default function EventsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal-elem');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const openModal = (e: React.MouseEvent, eventTitle: string) => {
    e.preventDefault();
    setSelectedEvent(eventTitle);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent('');
  };

  return (
    <section ref={sectionRef} id="events" className="py-20 md:py-24 bg-white font-sans relative">
      <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24 max-w-[1400px] mx-auto">
        
        {/* Section Header (Centered) */}
        <div className="reveal-elem opacity-0 translate-y-8 transition-all duration-700 ease-out max-w-3xl mx-auto text-center mb-14 flex flex-col items-center">
          <h2 
            style={{
              color: '#0F1C35',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              marginBottom: '16px'
            }}
          >
            Upcoming Events
          </h2>
        </div>

        {/* Cards — centered single card */}
        <div className="flex justify-center mb-14">
          <div className="w-full max-w-[480px]">
            {events.map((event, i) => (
              <div
                key={event.title}
                className="reveal-elem opacity-0 translate-y-8 transition-all duration-700 ease-out rounded-2xl overflow-hidden flex flex-col group hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                style={{ 
                  transitionDelay: `${150 + i * 150}ms`,
                  backgroundColor: '#F8F7F3',
                  border: '1px solid #E5E7EB'
                }}
              >
                {/* Card Image Wrapper */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-in-out"
                  />
                  {/* Event Category Tag */}
                  <div 
                    className="absolute top-4 left-4 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md text-white shadow-sm"
                    style={{ backgroundColor: '#0077b6' }}
                  >
                    {event.tag}
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6 md:p-8 flex flex-col flex-grow">
                  
                  {/* Calendar Date & Title Row */}
                  <div className="flex gap-5 mb-4 items-start">
                    {/* Calendar Block */}
                    <div 
                      className="flex flex-col items-center justify-center min-w-[60px] py-2 px-3 rounded-lg bg-white shadow-sm shrink-0"
                      style={{ border: '1px solid #E5E7EB' }}
                    >
                      <span className="text-2xl font-extrabold leading-none mb-1" style={{ color: '#0F1C35' }}>
                        {event.day}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#0077b6' }}>
                        {event.month.split(' ')[0]}
                      </span>
                      <span className="text-[9px] font-semibold" style={{ color: '#3D4A62' }}>
                        {event.month.split(' ')[1]}
                      </span>
                    </div>
                    
                    {/* Title */}
                    <h3 
                      className="text-lg md:text-xl font-bold leading-snug mt-1" 
                      style={{ color: '#0F1C35' }}
                    >
                      {event.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p 
                    className="text-sm leading-relaxed mb-8 flex-grow" 
                    style={{ color: '#3D4A62' }}
                  >
                    {event.desc}
                  </p>

                  {/* Action Button — only shown when hasRegistration is true */}
                  {event.hasRegistration && (
                    <button
                      onClick={(e) => openModal(e, event.title)}
                      className="inline-flex justify-center items-center px-6 py-3 w-full sm:w-auto text-white text-sm font-bold rounded-lg transition-opacity hover:opacity-90 cursor-pointer shadow-sm mt-auto"
                      style={{ backgroundColor: '#0077b6' }}
                    >
                      Register Now
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* View All Events Button */}
        <div className="reveal-elem opacity-0 translate-y-8 transition-all duration-700 ease-out text-center">
          <button
            onClick={() => navigate('/events')}
            className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-lg transition-all cursor-pointer shadow-md hover:shadow-lg group"
            style={{ 
              backgroundColor: '#0077b6',
              color: '#FFFFFF'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#0077b6';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#0077b6';
            }}
          >
            View All Events
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>

      </div>

      {/* Registration Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity">
          <div 
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-8 animate-in fade-in zoom-in-95 duration-200"
            style={{ border: '1px solid #E5E7EB' }}
          >
            {/* Close Button */}
            <button 
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h3 className="text-2xl font-bold mb-2" style={{ color: '#0F1C35' }}>
              Event Registration
            </h3>
            <p className="text-sm mb-6" style={{ color: '#0077b6' }}>
              {selectedEvent}
            </p>

            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); closeModal(); alert('Registration Submitted!'); }}>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#3D4A62' }}>Full Name</label>
                <input 
                  type="text" 
                  required
                  className="w-full px-4 py-2.5 rounded-lg border focus:ring-2 focus:outline-none transition-all"
                  style={{ borderColor: '#E5E7EB', outlineColor: '#0077b6' }}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#3D4A62' }}>Email Address</label>
                <input 
                  type="email" 
                  required
                  className="w-full px-4 py-2.5 rounded-lg border focus:ring-2 focus:outline-none transition-all"
                  style={{ borderColor: '#E5E7EB', outlineColor: '#0077b6' }}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#3D4A62' }}>Company / Organization</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-2.5 rounded-lg border focus:ring-2 focus:outline-none transition-all"
                  style={{ borderColor: '#E5E7EB', outlineColor: '#C9952A' }}
                  placeholder="Company Ltd."
                />
              </div>
              <div className="pt-4">
                <button 
                  type="submit"
                  className="w-full py-3 px-4 text-white font-bold rounded-lg transition-opacity hover:opacity-90 shadow-md"
                  style={{ backgroundColor: '#0F1C35' }}
                >
                  Submit Registration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}